import { useState } from 'react';
import styled from 'styled-components';

import { FilledBadge } from 'components/shared/Badge';
import Menu from 'components/shared/menu';
import useWorkflowTours from 'hooks/useWorkflowTours';
import { simpleDate } from 'lib/date';
import { TourOptions } from 'models/Tour';
import Workflow, { WorkflowGroupOptions } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronRight from 'svg/ChevronRight';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIconOutline from 'svg/PencilIconOutline';
import TrashIcon from 'svg/TrashIcon';

import { Caret } from '../categoryCard/styles';
import { EditLink } from '../ReferralSentTo';

import DeleteTourModal from './DeleteTourModal';
import TourModal from './TourModal';

function tourGroupName(workflow: Workflow, groupId: string) {
  return workflow.groups.find((group) => group.id === groupId)?.name ?? 'Unknown facility';
}

type Editing = { mode: 'log' } | { mode: 'edit'; tour: TourOptions; name: string };

export default function TourTrackingCard({ workflow }: { workflow: Workflow }) {
  const { enabled, canEdit, isLoaded, tours, groupIds } = useWorkflowTours(workflow);
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<TourOptions | null>(null);

  if (!enabled) return null;

  const rows = tours
    .map((tour) => ({ tour, name: tourGroupName(workflow, tour.groupId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const untoured: WorkflowGroupOptions[] = groupIds
    .filter((groupId) => !tours.some((tour) => tour.groupId === groupId))
    .map((groupId) => ({ id: groupId, name: tourGroupName(workflow, groupId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const allToured = isLoaded && groupIds.length > 0 && untoured.length === 0;

  return (
    <Card data-testid='tour-tracking-card'>
      <Header $open={expanded && isLoaded}>
        <Toggle type='button' onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}>
          <Caret $open={expanded}>
            <ChevronRight width={12} height={12} />
          </Caret>
          <BodySmallBold>Tour Tracking</BodySmallBold>
          {isLoaded && groupIds.length > 1 && tours.length > 0 && (
            <FilledBadge color='neutral' data-testid='tour-count'>
              {tours.length} of {groupIds.length} toured
            </FilledBadge>
          )}
        </Toggle>
        {canEdit && (
          <LogLink
            type='button'
            disabled={!isLoaded || untoured.length === 0}
            title={allToured ? 'Every facility on this referral already has a tour' : undefined}
            onClick={() => {
              setExpanded(true);
              setEditing({ mode: 'log' });
            }}
            data-testid='log-tour'>
            + Log Tour
          </LogLink>
        )}
      </Header>

      {expanded && isLoaded && (
        <>
          {rows.length === 0 && <Empty>Log a completed tour for any facility associated to this referral</Empty>}
          {rows.map(({ tour, name }) => (
            <Row key={tour.id} data-testid='tour-row'>
              <CircleCheckIcon width={12} height={12} fill={colors.green100} />
              <RowMain>
                <TourName data-testid='tour-name'>{name}</TourName>
                <RowDetail>
                  Toured – {simpleDate(tour.touredOn)} <Separator>•</Separator> Updated by {tour.updatedByName}
                </RowDetail>
              </RowMain>
              {canEdit && (
                <Menu $size='small'>
                  <Menu.Trigger>
                    <MenuButton type='button' aria-label={`Tour options for ${name}`} data-testid='tour-menu'>
                      <EllipsisIcon />
                    </MenuButton>
                  </Menu.Trigger>
                  <Menu.Content position='right'>
                    <Menu.Item data-testid='edit-tour-log' onClick={() => setEditing({ mode: 'edit', tour, name })}>
                      <Menu.Icon>
                        <PencilIconOutline width={14} height={14} />
                      </Menu.Icon>
                      Edit tour log
                    </Menu.Item>
                    <Menu.Divider />
                    <DeleteMenuItem data-testid='delete-tour-log' onClick={() => setDeleting(tour)}>
                      <Menu.Icon>
                        <TrashIcon width={14} height={14} color={colors.red100} />
                      </Menu.Icon>
                      Delete tour log
                    </DeleteMenuItem>
                  </Menu.Content>
                </Menu>
              )}
            </Row>
          ))}
        </>
      )}

      {editing?.mode === 'log' && <TourModal workflow={workflow} options={untoured} onClose={() => setEditing(null)} />}
      {editing?.mode === 'edit' && (
        <TourModal
          workflow={workflow}
          tour={editing.tour}
          facilityName={editing.name}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && <DeleteTourModal workflow={workflow} tour={deleting} onClose={() => setDeleting(null)} />}
    </Card>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};
`;

const Header = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${colors.black10}` : 'none')};
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: ${colors.black100};

  ${Caret} {
    margin-top: 0;
    color: ${colors.black100};
  }
`;

const TourName = styled(BodySmallBold)`
  font-size: 12px;
`;

const LogLink = styled(EditLink)`
  flex-shrink: 0;

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const Empty = styled(BodySmall)`
  display: block;
  padding: 20px;
  font-size: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 20px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }

  > svg {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const RowDetail = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black75};
`;

const Separator = styled.span`
  margin: 0 4px;
  color: ${colors.black25};
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${colors.black5};
  }
`;

const DeleteMenuItem = styled(Menu.Item)`
  color: ${colors.red100};
`;
